<script setup>
import { defineProps, defineEmits, watch } from 'vue';
import { required, email, regex } from 'vee-validate/dist/rules'
import { extend, ValidationProvider, ValidationObserver, setInteractionMode } from "vee-validate"

// Definir las propiedades que recibe el componente hijo
const props = defineProps(['title', 'form']);

// Emitir eventos desde el componente hijo
const emit = defineEmits();

// Usar watch para observar los cambios en la propiedad `title`
const cancel = () => {
  // Aquí emites el evento 'cancel' al componente padre, pasando '' como argumento
  emit('cancel' );
};
</script>

<template>
  <div>
    <v-card>
          <ValidationObserver ref="datos" v-slot="{ invalid, reset }">
              <v-form @submit.prevent="submitForm(reset)">
                  <v-card-title>
                      <span class="headline">Formulario de Registro</span>
                  </v-card-title>
                  <v-card-text>
                      <v-row>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Nombres" rules="required">
                                  <v-text-field v-model="form.nombres" label="Nombres" required outlined
                                      :error-messages="errors" prepend-icon="mdi-account"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Apellido Paterno"
                                  rules="required">
                                  <v-text-field v-model="form.apellidoPaterno" label="Apellido Paterno"
                                      required outlined :error-messages="errors"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Apellido Materno"
                                  rules="required">
                                  <v-text-field v-model="form.apellidoMaterno" label="Apellido Materno"
                                      required outlined :error-messages="errors"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Tipo de Documento"
                                  rules="required">
                                  <v-select v-model="form.tipoDocumento" :items="tipoDocumentos"
                                      item-text="vc_Descripcion" item-value="id" label="Tipo de Documento"
                                      required outlined :error-messages="errors"
                                      prepend-icon="mdi-card-account-details"></v-select>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Numero de documento"
                                  rules="required|numeric">
                                  <v-text-field v-model="form.numDocumento" label="Número de Documento"
                                      required outlined :error-messages="errors"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <v-menu ref="menuNacimiento" v-model="menuNacimiento"
                                  :close-on-content-click="false"
                                  :return-value.sync="form.fechaNacimiento" transition="scale-transition"
                                  offset-y min-width="auto">
                                  <template v-slot:activator="{ on, attrs }">
                                      <v-text-field v-model="form.fechaNacimiento"
                                          label="Fecha de Nacimiento" prepend-icon="mdi-calendar" readonly
                                          v-bind="attrs" v-on="on" outlined></v-text-field>
                                  </template>
                                  <v-date-picker v-model="form.fechaNacimiento" no-title scrollable>
                                      <v-spacer></v-spacer>
                                      <v-btn text color="primary"
                                          @click="menuNacimiento = false">Cancel</v-btn>
                                      <v-btn text color="primary"
                                          @click="$refs.menuNacimiento.save(form.fechaNacimiento)">OK</v-btn>
                                  </v-date-picker>
                              </v-menu>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Sexo" rules="required">
                                  <v-select v-model="form.sexo" :items="sexos" item-text="text"
                                      item-value="value" label="Sexo" required outlined
                                      :error-messages="errors"
                                      prepend-icon="mdi-clipboard-account"></v-select>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Correo Personal"
                                  rules="email">
                                  <v-text-field v-model="form.correoPersonal" label="Correo Personal"
                                      required outlined :error-messages="errors"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="4">
                              <ValidationProvider v-slot="{ errors }" name="Correo Corporativo"
                                  rules="required|email">
                                  <v-text-field v-model="form.correoCorporativo"
                                      label="Correo Corporativo" required outlined
                                      :error-messages="errors"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="6">
                              <ValidationProvider v-slot="{ errors }" name="Telefono"
                                  rules="required|numeric">
                                  <v-text-field v-model="form.telefono" label="Teléfono" required outlined
                                      :error-messages="errors"
                                      prepend-icon="mdi-card-account-phone"></v-text-field>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="6">
                              <ValidationProvider v-slot="{ errors }" name="Pais" rules="required">
                                  <v-select v-model="form.pais" :items="getPaises"
                                      item-text="vc_Descripcion" item-value="Id" label="País" required
                                      outlined :error-messages="errors"></v-select>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="6">
                              <ValidationProvider v-slot="{ errors }" name="Centro" rules="required">
                                  <v-select v-model="form.centro" :items="filteredCentros"
                                      item-text="vc_Descripcion" item-value="Id" label="Centro" required
                                      outlined :error-messages="errors" multiple chips
                                      prepend-icon="mdi-office-building"></v-select>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="6">
                              <ValidationProvider v-slot="{ errors }" name="Segmento" rules="required">
                                  <v-select v-model="form.segmento" :items="filteredSegmentos"
                                      item-text="vc_Descripcion" item-value="Id" label="Segmento" required
                                      outlined :error-messages="errors" multiple chips
                                      prepend-icon="mdi-office-building"></v-select>
                              </ValidationProvider>
                          </v-col>
                          <v-col cols="12" sm="6">
                              <ValidationProvider v-slot="{ errors }" name="Campaña" rules="required">
                                  <v-select v-model="form.campana" :items="filteredCampanas"
                                      item-text="vc_Descripcion" item-value="Id" label="Campaña" required
                                      outlined :error-messages="errors" multiple chips 
                                      prepend-icon="mdi-office-building"></v-select>
                              </ValidationProvider>
                          </v-col>
                      </v-row>
                  </v-card-text>
                  <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn @click="cancel(reset)" color="gray" outlined>Cancelar</v-btn>
                      <v-btn type="submit" color="green" class="white--text" :disabled="invalid"
                          :loading="loading">{{
                              form.id != null ?
                                  'Actualizar' : 'Guardar' }}</v-btn>
                  </v-card-actions>
              </v-form>
          </ValidationObserver>
      </v-card>
  </div>
</template>
